import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DailyTokenRow } from 'api/adminCopilot';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { map } from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';

Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface Props {
  data: DailyTokenRow[];
  height?: number;
}

const DailyTrendChart: React.FC<Props> = ({ data, height = 350 }) => {
  const theme = useTheme();
  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const chartData = useMemo(
    () => ({
      labels: map(data, 'date'),
      datasets: [
        {
          type: 'line' as const,
          label: 'Total Tokens',
          data: map(data, 'totalTokens'),
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.main,
          pointRadius: 3,
          tension: 0.3,
          yAxisID: 'y',
          order: 1,
        },
        {
          type: 'bar' as const,
          label: 'Sessions',
          data: map(data, 'sessionCount'),
          backgroundColor: `${theme.palette.secondary.main}66`,
          borderRadius: 3,
          yAxisID: 'y1',
          order: 2,
        },
      ],
    }),
    [data, theme]
  );

  const options: ChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        title: { display: true, text: 'Daily Token Usage Trend', color: textColor, font: { size: 14 } },
        legend: { position: 'bottom' as const, labels: { color: textColor } },
      },
      scales: {
        x: { ticks: { color: textColor }, grid: { color: gridColor } },
        y: {
          type: 'linear' as const,
          position: 'left' as const,
          title: { display: true, text: 'Total Tokens', color: textColor },
          ticks: { color: textColor },
          grid: { color: gridColor },
        },
        y1: {
          type: 'linear' as const,
          position: 'right' as const,
          title: { display: true, text: 'Sessions', color: textColor },
          ticks: { color: textColor },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    [textColor, gridColor]
  );

  if (data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No trend data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, position: 'relative' }}>
      <ReactChart type="bar" data={chartData} options={options} />
    </Box>
  );
};

export default DailyTrendChart;
